import type { Column, Row, Table } from "@tanstack/react-table";
import type { CSSProperties } from "react";

import type { Screening } from "@/types/screeners";
import { borderColorCodes } from "@/utils/ui";

export const getCommonPinningStyles = <T>(
  column: Column<T>,
  row?: Row<Screening>,
): CSSProperties => {
  const isPinned = column.getIsPinned();
  // We have to use box shadow rather than border to maintain it with sticky positioning
  const boxShadow = (() => {
    if (!isPinned) {
      return;
    }

    if (column.getIsFirstColumn("left") && row?.original.highlightColor) {
      return `inset 4px 0px 0px 0px ${borderColorCodes[row?.original.highlightColor]}`;
    }

    if (column.getIsLastColumn("left")) {
      return `inset -1px 0px 0px 0px var(--mantine-color-gray-0)`;
    }
  })();

  return {
    boxShadow,
    left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
    position: isPinned ? "sticky" : "relative",
    zIndex: isPinned ? 1 : 0,
  };
};

export const columnSizingHandler = <T>(
  tableHead: HTMLTableCellElement | null,
  table: Table<T>,
  column: Column<T>,
) => {
  // Only update columnSizing if the element's dimensions have actually changed to prevent an infinite loop.
  if (
    tableHead &&
    table.getState().columnSizing[column.id] !==
      tableHead.getBoundingClientRect().width
  ) {
    table.setColumnSizing((prevSizes) => ({
      ...prevSizes,
      [column.id]: tableHead.getBoundingClientRect().width,
    }));
  }
};
